import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Calculator, TrendingUp, FileText, Briefcase, AlertCircle,
  Plus, Trash2, RefreshCw, Loader2, Mail, Copy, Check, BookOpen,
} from 'lucide-react';
import { toast } from 'sonner';

const TABS = [
  { key: 'dashboard', label: 'Dashboard', icon: TrendingUp },
  { key: 'devis', label: 'Devis cash-aware', icon: Calculator },
  { key: 'projects', label: 'Projets en cours', icon: Briefcase },
  { key: 'whatif', label: 'What-if', icon: RefreshCw },
  { key: 'comptable', label: 'Point comptable', icon: Mail },
];

const fmt = (n) => (n == null ? '—' : new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n));

const CFO = () => {
  const { api } = useAuth();
  const [tab, setTab] = useState('dashboard');
  const [templates, setTemplates] = useState([]);
  const [defaultKey, setDefaultKey] = useState('custom');
  const [projects, setProjects] = useState([]);
  const [bfrTotal, setBfrTotal] = useState(0);
  const [pipelineCash, setPipelineCash] = useState(0);
  const [bfrAlert, setBfrAlert] = useState(null);
  const [monthly, setMonthly] = useState([]);

  // devis form
  const [tplKey, setTplKey] = useState('custom');
  const [form, setForm] = useState({
    title: '', client_name: '',
    prix_vente_ht: '', tva_vente: 21, achat_materiel_ht: '', tva_achat: 21,
    sous_traitance: 0, sous_traitance_with_vat: false,
    delai_paiement_client_jours: 30, delai_paiement_fournisseur_jours: 30,
    statut: 'devis',
  });
  const [analysis, setAnalysis] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);

  // monthly form
  const currentMonth = new Date().toISOString().slice(0, 7);
  const [mform, setMform] = useState({ month: currentMonth });

  // what-if
  const [wif, setWif] = useState({ base_revenue: '', base_margin_pct: 30, base_burn: '', base_treasury: '', delta_revenue_pct: 0, delta_margin_pct: 0, delta_burn_pct: 0 });
  const [wifResult, setWifResult] = useState(null);

  // accountant email
  const [emailContent, setEmailContent] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);

  useEffect(() => {
    api.get('/api/cfo/templates').then(r => {
      setTemplates(r.data?.templates || []);
      setDefaultKey(r.data?.default_key || 'custom');
      setTplKey(r.data?.default_key || 'custom');
    }).catch(() => {});
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = async () => {
    try {
      const [pr, mr] = await Promise.all([
        api.get('/api/cfo/projects'),
        api.get('/api/cfo/monthly'),
      ]);
      setProjects(pr.data?.projects || []);
      setBfrTotal(pr.data?.bfr_total_mobilise || 0);
      setPipelineCash(pr.data?.cash_in_pipeline || 0);
      setBfrAlert(pr.data?.bfr_alert || null);
      setMonthly(mr.data?.months || []);
    } catch { /* noop */ }
  };

  const applyTemplate = (key) => {
    setTplKey(key);
    const tpl = templates.find(t => t.key === key);
    if (!tpl) return;
    setForm(prev => ({
      ...prev,
      tva_vente: tpl.tva_vente,
      tva_achat: tpl.tva_achat,
      sous_traitance_with_vat: !tpl.autoliquidation,
      delai_paiement_client_jours: tpl.delai_client,
      delai_paiement_fournisseur_jours: tpl.delai_fournisseur,
    }));
  };

  const calcCash = async () => {
    if (!form.prix_vente_ht || form.prix_vente_ht <= 0) {
      toast.error('Renseigne au moins un prix de vente HT');
      return;
    }
    setCalcLoading(true);
    try {
      const payload = {
        ...form,
        prix_vente_ht: parseFloat(form.prix_vente_ht) || 0,
        achat_materiel_ht: parseFloat(form.achat_materiel_ht) || 0,
        sous_traitance: parseFloat(form.sous_traitance) || 0,
        tva_vente: parseFloat(form.tva_vente) || 0,
        tva_achat: parseFloat(form.tva_achat) || 0,
        delai_paiement_client_jours: parseInt(form.delai_paiement_client_jours, 10) || 0,
        delai_paiement_fournisseur_jours: parseInt(form.delai_paiement_fournisseur_jours, 10) || 0,
      };
      const r = await api.post('/api/cfo/calc-cash', payload);
      setAnalysis(r.data);
    } catch { toast.error('Calcul impossible'); }
    finally { setCalcLoading(false); }
  };

  const saveProject = async () => {
    if (!form.title) { toast.error('Donne un titre au projet'); return; }
    try {
      await api.post('/api/cfo/projects', {
        ...form,
        prix_vente_ht: parseFloat(form.prix_vente_ht) || 0,
        achat_materiel_ht: parseFloat(form.achat_materiel_ht) || 0,
        sous_traitance: parseFloat(form.sous_traitance) || 0,
        tva_vente: parseFloat(form.tva_vente) || 0,
        tva_achat: parseFloat(form.tva_achat) || 0,
        delai_paiement_client_jours: parseInt(form.delai_paiement_client_jours, 10) || 0,
        delai_paiement_fournisseur_jours: parseInt(form.delai_paiement_fournisseur_jours, 10) || 0,
      });
      toast.success('Projet enregistré');
      refresh();
      setTab('projects');
    } catch { toast.error('Échec enregistrement'); }
  };

  const deleteProject = async (id) => {
    if (!confirm('Supprimer ce projet ?')) return;
    await api.delete(`/api/cfo/projects/${id}`);
    refresh();
  };

  const saveMonthly = async () => {
    const payload = { ...mform };
    Object.keys(payload).forEach(k => {
      if (payload[k] === '' || payload[k] == null) delete payload[k];
      else if (k !== 'month') payload[k] = parseFloat(payload[k]) || 0;
    });
    try {
      await api.put('/api/cfo/monthly', payload);
      toast.success('Mois mis à jour');
      refresh();
    } catch { toast.error('Échec'); }
  };

  const runWhatIf = async () => {
    try {
      const payload = Object.fromEntries(
        Object.entries(wif).map(([k, v]) => [k, parseFloat(v) || 0])
      );
      const r = await api.post('/api/cfo/whatif', payload);
      setWifResult(r.data);
    } catch { toast.error('Calcul impossible'); }
  };

  const generateAccountantEmail = async () => {
    setEmailLoading(true);
    setEmailContent('');
    try {
      const r = await api.post('/api/cfo/accountant-email', {}, { timeout: 110000 });
      setEmailContent(r.data?.content || '');
    } catch { toast.error('Génération impossible'); }
    finally { setEmailLoading(false); }
  };

  const copyEmail = async () => {
    await navigator.clipboard.writeText(emailContent);
    setEmailCopied(true);
    toast.success('Copié');
    setTimeout(() => setEmailCopied(false), 2000);
  };

  const lastMonth = monthly[0] || {};
  const totalCharges = ['charges_salaires', 'charges_loyer', 'charges_fournisseurs', 'charges_marketing', 'charges_autres']
    .reduce((s, k) => s + (lastMonth[k] || 0), 0);
  const cashFlow = (lastMonth.ca_realise || 0) - totalCharges;

  return (
    <div data-testid="cfo-page" className="p-6 lg:p-8 max-w-6xl mx-auto space-y-6">
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Calculator className="w-6 h-6 text-atlasCyan" />
          <h1 className="text-3xl font-bold text-white">Mode CFO</h1>
          <span className="text-[10px] px-2 py-1 rounded-full bg-atlasCyan/15 text-atlasCyan font-medium tracking-wide">DIRECTEUR FINANCIER STRATÉGIQUE</span>
        </div>
        <p className="text-atlasTextSecondary">Lecture cash-aware, alertes proactives, pédagogie financière. Pour ne plus jamais rater un signal faible.</p>
      </div>

      <div className="glass-card p-4 border-l-4 border-l-atlasCyan/60 flex items-start gap-3">
        <BookOpen className="w-4 h-4 text-atlasCyan flex-shrink-0 mt-0.5" />
        <p className="text-xs text-atlasTextSecondary leading-relaxed">
          ATLAS CFO est ton bras stratégique, pas ton expert-comptable légal. Pour la production officielle des comptes, déclarations et conseil fiscal personnalisé, ton expert-comptable reste indispensable.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-atlasBorder overflow-x-auto">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.key} data-testid={`cfo-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-3 font-medium text-sm transition-colors border-b-2 -mb-px whitespace-nowrap ${tab === t.key ? 'text-atlasCyan border-atlasCyan' : 'text-atlasTextSecondary border-transparent hover:text-white'}`}>
              <Icon className="w-4 h-4" />{t.label}
            </button>
          );
        })}
      </div>

      {/* Garde-fou stratégique : alerte BFR proactive */}
      {bfrAlert && (tab === 'dashboard' || tab === 'projects') && (
        <div
          data-testid="cfo-bfr-alert"
          data-severity={bfrAlert.severity}
          className={`rounded-xl p-4 lg:p-5 border flex items-start gap-3 ${
            bfrAlert.severity === 'danger'
              ? 'bg-red-500/10 border-red-500/40'
              : bfrAlert.severity === 'vigilance'
              ? 'bg-orange-500/10 border-orange-500/40'
              : 'bg-atlasCyan/10 border-atlasCyan/30'
          }`}
        >
          <AlertCircle
            className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
              bfrAlert.severity === 'danger'
                ? 'text-red-400'
                : bfrAlert.severity === 'vigilance'
                ? 'text-orange-400'
                : 'text-atlasCyan'
            }`}
          />
          <div className="flex-1 min-w-0">
            <p
              className={`text-sm font-semibold mb-1 ${
                bfrAlert.severity === 'danger'
                  ? 'text-red-300'
                  : bfrAlert.severity === 'vigilance'
                  ? 'text-orange-300'
                  : 'text-atlasCyan'
              }`}
            >
              {bfrAlert.headline}
            </p>
            <p className="text-[13px] text-white/85 leading-relaxed">{bfrAlert.message}</p>
            {bfrAlert.cta_tab && bfrAlert.cta_tab !== tab && (
              <button
                data-testid="cfo-bfr-alert-cta"
                onClick={() => setTab(bfrAlert.cta_tab)}
                className="text-xs text-white/70 hover:text-white underline underline-offset-2 mt-2"
              >
                {bfrAlert.cta_label} →
              </button>
            )}
          </div>
        </div>
      )}

      {tab === 'dashboard' && (
        <>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="glass-card p-5"><p className="text-xs text-atlasTextSecondary mb-1">CA réalisé ({lastMonth.month || currentMonth})</p><p className="text-2xl font-bold text-white">{fmt(lastMonth.ca_realise)}</p></div>
            <div className="glass-card p-5"><p className="text-xs text-atlasTextSecondary mb-1">Trésorerie</p><p className="text-2xl font-bold text-white">{fmt(lastMonth.treasury)}</p></div>
            <div className="glass-card p-5"><p className="text-xs text-atlasTextSecondary mb-1">Cash-flow estimé</p><p className={`text-2xl font-bold ${cashFlow >= 0 ? 'text-atlasCyan' : 'text-red-400'}`}>{fmt(cashFlow)}</p></div>
            <div className="glass-card p-5"><p className="text-xs text-atlasTextSecondary mb-1">DSO</p><p className="text-2xl font-bold text-white">{lastMonth.dso_jours ? `${lastMonth.dso_jours} j` : '—'}</p></div>
            <div className="glass-card p-5"><p className="text-xs text-atlasTextSecondary mb-1">BFR mobilisé (projets)</p><p className="text-2xl font-bold text-white">{fmt(bfrTotal)}</p></div>
            <div className="glass-card p-5"><p className="text-xs text-atlasTextSecondary mb-1">Cash dans le pipeline</p><p className="text-2xl font-bold text-atlasCyan">{fmt(pipelineCash)}</p></div>
          </div>

          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Mise à jour mensuelle</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div><Label className="text-atlasTextSecondary text-sm">Mois</Label><Input type="month" value={mform.month} onChange={e => setMform({...mform, month: e.target.value})} className="input-atlas mt-1" /></div>
              <div><Label className="text-atlasTextSecondary text-sm">CA réalisé HT (€)</Label><Input type="number" value={mform.ca_realise || ''} onChange={e => setMform({...mform, ca_realise: e.target.value})} className="input-atlas mt-1" /></div>
              <div><Label className="text-atlasTextSecondary text-sm">Trésorerie (€)</Label><Input type="number" value={mform.treasury || ''} onChange={e => setMform({...mform, treasury: e.target.value})} className="input-atlas mt-1" /></div>
              <div><Label className="text-atlasTextSecondary text-sm">Charges salaires (€)</Label><Input type="number" value={mform.charges_salaires || ''} onChange={e => setMform({...mform, charges_salaires: e.target.value})} className="input-atlas mt-1" /></div>
              <div><Label className="text-atlasTextSecondary text-sm">Loyer (€)</Label><Input type="number" value={mform.charges_loyer || ''} onChange={e => setMform({...mform, charges_loyer: e.target.value})} className="input-atlas mt-1" /></div>
              <div><Label className="text-atlasTextSecondary text-sm">Fournisseurs (€)</Label><Input type="number" value={mform.charges_fournisseurs || ''} onChange={e => setMform({...mform, charges_fournisseurs: e.target.value})} className="input-atlas mt-1" /></div>
              <div><Label className="text-atlasTextSecondary text-sm">Marketing (€)</Label><Input type="number" value={mform.charges_marketing || ''} onChange={e => setMform({...mform, charges_marketing: e.target.value})} className="input-atlas mt-1" /></div>
              <div><Label className="text-atlasTextSecondary text-sm">Autres charges (€)</Label><Input type="number" value={mform.charges_autres || ''} onChange={e => setMform({...mform, charges_autres: e.target.value})} className="input-atlas mt-1" /></div>
              <div><Label className="text-atlasTextSecondary text-sm">DSO (jours)</Label><Input type="number" value={mform.dso_jours || ''} onChange={e => setMform({...mform, dso_jours: e.target.value})} className="input-atlas mt-1" /></div>
              <div><Label className="text-atlasTextSecondary text-sm">Encaissements en attente (€)</Label><Input type="number" value={mform.encaissements_attente || ''} onChange={e => setMform({...mform, encaissements_attente: e.target.value})} className="input-atlas mt-1" /></div>
              <div><Label className="text-atlasTextSecondary text-sm">Engagements à payer (€)</Label><Input type="number" value={mform.engagements_a_payer || ''} onChange={e => setMform({...mform, engagements_a_payer: e.target.value})} className="input-atlas mt-1" /></div>
            </div>
            <Button data-testid="cfo-save-monthly" onClick={saveMonthly} className="btn-primary mt-4">Enregistrer le mois</Button>
          </div>
        </>
      )}

      {tab === 'devis' && (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="glass-card p-6 space-y-4">
            <h3 className="text-lg font-semibold text-white">Simuler un devis</h3>
            <div>
              <Label className="text-atlasTextSecondary text-sm">Template sectoriel</Label>
              <select data-testid="cfo-template-select" value={tplKey} onChange={e => applyTemplate(e.target.value)} className="input-atlas mt-1 w-full px-3 py-2 rounded-md bg-atlasBg border border-atlasBorder text-white">
                {templates.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
              <p className="text-[11px] text-atlasTextSecondary mt-1">{templates.find(t => t.key === tplKey)?.hint}</p>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div><Label className="text-atlasTextSecondary text-sm">Titre du chantier</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="input-atlas mt-1" placeholder="Devis Caroline Etienne" /></div>
              <div><Label className="text-atlasTextSecondary text-sm">Client</Label><Input value={form.client_name} onChange={e => setForm({...form, client_name: e.target.value})} className="input-atlas mt-1" /></div>
              <div><Label className="text-atlasTextSecondary text-sm">Prix vente HT (€)</Label><Input type="number" data-testid="cfo-prix-vente" value={form.prix_vente_ht} onChange={e => setForm({...form, prix_vente_ht: e.target.value})} className="input-atlas mt-1" /></div>
              <div><Label className="text-atlasTextSecondary text-sm">TVA vente (%)</Label><Input type="number" step="0.5" value={form.tva_vente} onChange={e => setForm({...form, tva_vente: e.target.value})} className="input-atlas mt-1" /></div>
              <div><Label className="text-atlasTextSecondary text-sm">Achat matériel HT (€)</Label><Input type="number" data-testid="cfo-achat-mat" value={form.achat_materiel_ht} onChange={e => setForm({...form, achat_materiel_ht: e.target.value})} className="input-atlas mt-1" /></div>
              <div><Label className="text-atlasTextSecondary text-sm">TVA achat (%)</Label><Input type="number" step="0.5" value={form.tva_achat} onChange={e => setForm({...form, tva_achat: e.target.value})} className="input-atlas mt-1" /></div>
              <div><Label className="text-atlasTextSecondary text-sm">Sous-traitance (€)</Label><Input type="number" data-testid="cfo-sous-trait" value={form.sous_traitance} onChange={e => setForm({...form, sous_traitance: e.target.value})} className="input-atlas mt-1" /></div>
              <div className="flex items-end pb-2">
                <label className="flex items-center gap-2 text-xs text-atlasTextSecondary cursor-pointer">
                  <input type="checkbox" checked={form.sous_traitance_with_vat} onChange={e => setForm({...form, sous_traitance_with_vat: e.target.checked})} />
                  TVA sur sous-traitance (sinon autoliquidation)
                </label>
              </div>
              <div><Label className="text-atlasTextSecondary text-sm">Délai client (j)</Label><Input type="number" value={form.delai_paiement_client_jours} onChange={e => setForm({...form, delai_paiement_client_jours: e.target.value})} className="input-atlas mt-1" /></div>
              <div><Label className="text-atlasTextSecondary text-sm">Délai fournisseur (j)</Label><Input type="number" value={form.delai_paiement_fournisseur_jours} onChange={e => setForm({...form, delai_paiement_fournisseur_jours: e.target.value})} className="input-atlas mt-1" /></div>
            </div>
            <div className="flex gap-2">
              <Button data-testid="cfo-calc-btn" onClick={calcCash} disabled={calcLoading} className="btn-primary flex-1">
                {calcLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Analyser le cash'}
              </Button>
              <Button onClick={saveProject} variant="outline" className="border-atlasCyan/40 text-atlasCyan"><Plus className="w-4 h-4 mr-1" /> Enregistrer comme projet</Button>
            </div>
          </div>

          {analysis && (
            <div className="glass-card p-6 space-y-3" data-testid="cfo-analysis">
              <h3 className="text-lg font-semibold text-white mb-2">Analyse cash-aware</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-atlasTextSecondary">Marge comptable</span><span className="text-white font-semibold">{fmt(analysis.marge_comptable)} ({analysis.marge_pct}%)</span></div>
                <div className="flex justify-between"><span className="text-atlasTextSecondary">Prix de vente TTC</span><span className="text-white">{fmt(analysis.prix_vente_ttc)}</span></div>
                <div className="flex justify-between"><span className="text-atlasTextSecondary">Coût total TTC à avancer</span><span className="text-white">{fmt(analysis.achat_materiel_ttc + analysis.sous_traitance_ttc)}</span></div>
                <div className="border-t border-atlasBorder pt-2 mt-2"></div>
                <div className="flex justify-between"><span className="text-atlasTextSecondary">TVA collectée (à reverser)</span><span className="text-white">{fmt(analysis.tva_collectee)}</span></div>
                <div className="flex justify-between"><span className="text-atlasTextSecondary">TVA déductible (à récupérer)</span><span className="text-white">{fmt(analysis.tva_deductible)}</span></div>
                {analysis.tva_nette_credit > 0 && <div className="flex justify-between text-atlasCyan"><span>Crédit TVA récupérable</span><span>{fmt(analysis.tva_nette_credit)}</span></div>}
                {analysis.tva_nette_a_payer > 0 && <div className="flex justify-between text-orange-400"><span>TVA nette à payer</span><span>{fmt(analysis.tva_nette_a_payer)}</span></div>}
              </div>
              <div className="border-t border-atlasBorder pt-3 space-y-2">
                <div className="flex justify-between"><span className="text-white font-semibold">BFR mobilisé</span><span className="text-orange-400 font-bold text-lg">{fmt(analysis.bfr_mobilise)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-atlasTextSecondary">Délai cash-positif estimé</span><span className="text-white">~{analysis.delai_cash_positif_jours} jours</span></div>
                <div className="flex justify-between text-sm"><span className="text-atlasTextSecondary">ROIC annualisé</span><span className="text-atlasCyan">{analysis.roic_annualise_pct}%</span></div>
              </div>
              <div className="bg-atlasCyan/5 border border-atlasCyan/20 rounded-lg p-3 mt-3">
                <p className="text-[12px] text-white leading-relaxed">
                  💡 <strong>Lecture cash :</strong> tu décaisses {fmt(analysis.bfr_mobilise)} avant d'encaisser. Si tu lances 5 chantiers similaires en parallèle, tu mobilises {fmt(analysis.bfr_mobilise * 5)} de BFR permanent. Marge ≠ trésorerie.
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'projects' && (
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="glass-card p-5"><p className="text-xs text-atlasTextSecondary mb-1">BFR mobilisé total</p><p className="text-3xl font-bold text-orange-400">{fmt(bfrTotal)}</p></div>
            <div className="glass-card p-5"><p className="text-xs text-atlasTextSecondary mb-1">Cash entrant pipeline</p><p className="text-3xl font-bold text-atlasCyan">{fmt(pipelineCash)}</p></div>
          </div>
          <div className="glass-card p-6">
            {projects.length === 0 ? (
              <p className="text-atlasTextSecondary text-sm text-center py-6">Aucun projet enregistré. Va dans <strong className="text-white">Devis cash-aware</strong> pour en créer.</p>
            ) : (
              <ul className="space-y-3">
                {projects.map(p => (
                  <li key={p.id} className="p-4 rounded-lg bg-white/[0.02] border border-atlasBorder">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="min-w-0">
                        <p className="text-white font-medium truncate">{p.title}</p>
                        <p className="text-xs text-atlasTextSecondary">{p.client_name || '—'} · <span className="capitalize">{p.statut}</span></p>
                      </div>
                      <button onClick={() => deleteProject(p.id)} className="text-red-400 hover:bg-red-500/10 p-1.5 rounded"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <div className="grid grid-cols-3 gap-3 text-xs">
                      <div><span className="text-atlasTextSecondary">Vente HT</span><p className="text-white font-semibold">{fmt(p.prix_vente_ht)}</p></div>
                      <div><span className="text-atlasTextSecondary">Marge</span><p className="text-white font-semibold">{fmt(p.analysis?.marge_comptable)}</p></div>
                      <div><span className="text-atlasTextSecondary">BFR mobilisé</span><p className="text-orange-400 font-semibold">{fmt(p.analysis?.bfr_mobilise)}</p></div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {tab === 'whatif' && (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="glass-card p-6 space-y-3">
            <h3 className="text-lg font-semibold text-white">Scénario "what-if"</h3>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-atlasTextSecondary text-xs">CA mensuel actuel (€)</Label><Input type="number" value={wif.base_revenue} onChange={e => setWif({...wif, base_revenue: e.target.value})} className="input-atlas mt-1" /></div>
              <div><Label className="text-atlasTextSecondary text-xs">Marge brute (%)</Label><Input type="number" value={wif.base_margin_pct} onChange={e => setWif({...wif, base_margin_pct: e.target.value})} className="input-atlas mt-1" /></div>
              <div><Label className="text-atlasTextSecondary text-xs">Charges fixes / mois (€)</Label><Input type="number" value={wif.base_burn} onChange={e => setWif({...wif, base_burn: e.target.value})} className="input-atlas mt-1" /></div>
              <div><Label className="text-atlasTextSecondary text-xs">Trésorerie (€)</Label><Input type="number" value={wif.base_treasury} onChange={e => setWif({...wif, base_treasury: e.target.value})} className="input-atlas mt-1" /></div>
            </div>
            <div className="border-t border-atlasBorder pt-3">
              <p className="text-white text-sm mb-2 font-medium">Et si...</p>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-atlasTextSecondary text-xs">Δ CA (%)</Label><Input type="number" value={wif.delta_revenue_pct} onChange={e => setWif({...wif, delta_revenue_pct: e.target.value})} className="input-atlas mt-1" placeholder="-5" /></div>
                <div><Label className="text-atlasTextSecondary text-xs">Δ Marge (pts)</Label><Input type="number" value={wif.delta_margin_pct} onChange={e => setWif({...wif, delta_margin_pct: e.target.value})} className="input-atlas mt-1" placeholder="-2" /></div>
                <div><Label className="text-atlasTextSecondary text-xs">Δ Charges (%)</Label><Input type="number" value={wif.delta_burn_pct} onChange={e => setWif({...wif, delta_burn_pct: e.target.value})} className="input-atlas mt-1" placeholder="+10" /></div>
              </div>
            </div>
            <Button onClick={runWhatIf} className="btn-primary w-full">Calculer l'impact</Button>
          </div>
          {wifResult && (
            <div className="glass-card p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Comparaison</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <p className="text-xs text-atlasTextSecondary">Aujourd'hui</p>
                  <p className="text-sm text-white">CA: <strong>{fmt(wifResult.base.revenue)}</strong></p>
                  <p className="text-sm text-white">Net mensuel: <strong className={wifResult.base.net_monthly < 0 ? 'text-red-400' : 'text-atlasCyan'}>{fmt(wifResult.base.net_monthly)}</strong></p>
                  <p className="text-sm text-white">Runway: <strong>{wifResult.base.runway_days >= 999 ? '∞' : `${wifResult.base.runway_days}j`}</strong></p>
                </div>
                <div className="space-y-2">
                  <p className="text-xs text-atlasCyan">Scénario</p>
                  <p className="text-sm text-white">CA: <strong>{fmt(wifResult.scenario.revenue)}</strong></p>
                  <p className="text-sm text-white">Net mensuel: <strong className={wifResult.scenario.net_monthly < 0 ? 'text-red-400' : 'text-atlasCyan'}>{fmt(wifResult.scenario.net_monthly)}</strong></p>
                  <p className="text-sm text-white">Runway: <strong>{wifResult.scenario.runway_days >= 999 ? '∞' : `${wifResult.scenario.runway_days}j`}</strong></p>
                </div>
              </div>
              <div className={`mt-4 p-3 rounded-lg ${wifResult.delta_net_monthly >= 0 ? 'bg-atlasCyan/10' : 'bg-red-500/10'}`}>
                <p className="text-sm text-white">Impact net mensuel : <strong className={wifResult.delta_net_monthly >= 0 ? 'text-atlasCyan' : 'text-red-400'}>{fmt(wifResult.delta_net_monthly)}</strong></p>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'comptable' && (
        <div className="space-y-4">
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold text-white mb-2">Préparer ton point comptable</h3>
            <p className="text-sm text-atlasTextSecondary mb-4">ATLAS génère un email stratégique pré-rédigé pour ton expert-comptable. À toi de l'ajuster, le copier, l'envoyer.</p>
            <Button onClick={generateAccountantEmail} disabled={emailLoading} className="btn-primary">
              {emailLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Génération…</> : <><FileText className="w-4 h-4 mr-2" />Générer l'email</>}
            </Button>
          </div>
          {emailContent && (
            <div className="glass-card p-6">
              <div className="flex items-center justify-between mb-3">
                <p className="text-white font-medium">Email prêt</p>
                <button onClick={copyEmail} className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-md bg-atlasCyan/10 text-atlasCyan hover:bg-atlasCyan/20">
                  {emailCopied ? <><Check className="w-3.5 h-3.5" />Copié</> : <><Copy className="w-3.5 h-3.5" />Copier</>}
                </button>
              </div>
              <pre className="text-white text-sm whitespace-pre-wrap font-sans leading-relaxed bg-atlasBg/50 p-4 rounded-lg border border-atlasBorder">{emailContent}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CFO;
